import { assert } from "@/utils/tools"
import io, { type Socket } from "socket.io-client"
import { _DEFAULT_CONN_CONFIG } from "./constants"
import { _OnRemotePeerCallback } from "./webrtc"
import { _reportSDKLog } from "./index"

// 信令服务器接收的消息类型
export interface _SignalingMessage {
  master: boolean
  room: string
  src: string
  // 目标设备 id（信封字段，用于地址过滤，对齐 android envelope["device_id"]）
  device_id?: string
  data: string
}

interface Status {
  room: string
  id: string
  online: boolean
  ret: number
}

// 远程协助状态（枚举值即为信令中的 status 字符串，对齐 android KCAssistStatus）
export type KCAssistStatus =
  | "started" // 管理后台发起协助请求（后台 -> 客户端）
  | "confirmed" // 用户确认同意远程协助（客户端 -> 后台）
  | "denied" // 用户拒绝远程协助（客户端 -> 后台）
  | "aborted" // 管理后台中断了远程协助（后台 -> 客户端）
  | "connected" // 售后成功连接到云电脑（售后 -> 客户端）

const ASSIST_STATUS_VALUES: KCAssistStatus[] = [
  "started",
  "confirmed",
  "denied",
  "aborted",
  "connected",
]

// 字符串 -> 枚举值，未知返回 null（对齐 android KCAssistStatus.fromValue）
function assistStatusFromValue(value: unknown): KCAssistStatus | null {
  return ASSIST_STATUS_VALUES.includes(value as KCAssistStatus)
    ? (value as KCAssistStatus)
    : null
}

// 远程协助消息回调（对齐 android OnAssistMessage）
// [status] 协助状态
// [data] 消息业务体
// [src] 发送方的 deviceId（信令信封里的 src）
export type OnAssistMessage = (
  status: KCAssistStatus,
  data: any,
  src: string | null
) => void

export class _KCDeskSignaling {
  /** 处理信令服务相关的逻辑 */

  private socket: Socket | null = null

  // 当前连接的用户
  private localDevId: string | null = null

  // 目标云电脑 deviceId（agent room），用于 agent_room_id header 与协助身份
  private remoteDevId: string | null = null

  // 是否为协助方登录模式（角色B：售后以协助身份登录）。
  // 由 SDK 门面层设置，影响 device_id header 与消息 src，对齐 android 不在信令层
  // 直接依赖全局状态的设计。desktop 端默认 false（不引入角色B）。
  private assistMode = false

  // WebRTC 连接过程中的远端 SDP 的回调
  private remotePeerCallback: _OnRemotePeerCallback | null = null

  // 远程协助消息回调（独立于 remotePeerCallback，生命周期随信令连接常驻）
  private onAssistMessage: OnAssistMessage | null = null

  private heartbeatTimeout: number | null = null

  private _isConnected = false
  private resolveConnect: ((value: boolean) => void) | null = null
  // Socket.IO 重连窗口内 ICE/Offer 仍可能产生；暂存后在底层 socket
  // 真正 connected 时发送，避免候选丢失或异常冒泡到 UI。
  private pendingMessages: Array<{ event: string; data: any }> = []

  // 网络监听驱动断开/重连（对齐 android _onNetworkStatusChange）：
  // 断网时主动 close socket 避免退避等待，恢复时主动 connect 立即重连。
  // 需要保存 connect 参数以便 online 时重建连接。
  private savedToken: string | null = null
  private networkBound = false
  private readonly onNetworkOffline = (): void => this.handleNetworkOffline()
  private readonly onNetworkOnline = (): void => this.handleNetworkOnline()

  get isConnected(): boolean {
    return this._isConnected
  }

  // 设置协助方登录模式（角色B），由 SDK 门面层在 connect 前调用
  setAssistMode(mode: boolean): void {
    this.assistMode = mode
  }

  // 连接信令服务器
  // [remoteDevId] 目标云电脑 deviceId，用于 agent_room_id header（确保同 room）
  connect(localDevId: string, token: string, remoteDevId: string): void {
    if (this.socket != null) {
      return
    }

    // 上报信令开始连接
    _reportSDKLog("signal_connect")

    this.localDevId = localDevId
    this.remoteDevId = remoteDevId
    this.savedToken = token

    try {
      this.socket = io(_DEFAULT_CONN_CONFIG.signalingServer, {
        // 注意：浏览器环境的 WebSocket API 无法设置自定义 header，若强制
        // transports:["websocket"] 会导致 extraHeaders 里的鉴权信息（x_token/
        // device_id/agent_room_id）无法传递，服务端返回 unauthorized。
        // 因此不强制 websocket-only，沿用 socket.io 默认的 polling 握手（带
        // extraHeaders 鉴权）→ websocket 升级。android 端 Dart socket_io_client
        // 的 WS 原生支持 header，故可 setTransports(["websocket"])，浏览器不可。
        // 每次创建全新连接，避免复用旧 Manager 导致状态污染（对齐 android enableForceNew）
        forceNew: true,
        reconnectionDelay: 500,
        reconnectionDelayMax: 5000,
        // agent_room_id 通过 HTTP header 传递，与 android 完全一致。
        // 服务端在握手时从该 header 把客户端加入云电脑 room，从而能收到发给
        // 该 room 的远程协助(assist)消息（assist 是云电脑 room 广播；而
        // answer/candidate 按 device_id 点对点路由，不依赖 room）。
        //
        // 方案 B：服务端在 Access-Control-Allow-Headers 放行 agent_room_id，
        // 浏览器 polling 握手时该 header 不再触发预检失败。若仍报
        // "agent_room_id is not allowed"，说明服务端配置未真正生效（未部署 /
        // 改错层 / 被 nginx 覆盖），需服务端排查。
        extraHeaders: {
          // 协助方登录模式下 device_id 带 -assist 后缀，让远端识别协助身份
          device_id: this.assistMode
            ? `${remoteDevId}-assist`
            : localDevId,
          // 确保与云电脑处于同一个 room 中（对齐 android agent_room_id header）
          agent_room_id: remoteDevId,
          x_token: token,
        },
      })
    } catch (e) {
      // 连接创建异常时兜底清理，避免悬挂引用（对齐 android _innerConnect try-catch）
      console.error("Signaling connect error: ", e)
      this.close()
      return
    }

    this.socket.on("connect", () => {
      // 打印握手传输，确认连接已建立（agent_room_id 是否进 query 请看 Network 握手 URL）
      const transport = this.socket?.io?.engine?.transport?.name
      console.log(
        `Signaling user ${localDevId} connected, agent_room_id=${remoteDevId}, transport=${transport}`
      )
      this.onConnect()
    })

    this.socket.on("status", this.onStatus.bind(this))
    this.socket.on("heartbeat", this.onHeartBeat.bind(this))
    this.socket.on("message", this.onMessage.bind(this))

    this.socket.on("data", (data) => {
      console.log("Signaling receive data: ", data)
    })

    this.socket.on("disconnect", this.onDisconnect.bind(this))
    this.socket.on("connect_error", this.onConnectError.bind(this))
    this.socket.on("error", this.onConnectError.bind(this))

    this.socket.io.on("error", this.onConnectError.bind(this))

    this.socket.io.on("reconnect", this.onReconnect.bind(this))
    this.socket.io.on("reconnect_error", this.onConnectError.bind(this))

    // 注册网络监听（断网主动断开、恢复主动重连），对齐 android
    // _onNetworkStatusChange。close 时移除。
    this.bindNetworkListeners()
  }

  // 关闭信令服务器连接
  close(): void {
    console.log("Signaling close connection")
    if (this.heartbeatTimeout !== null) {
      window.clearTimeout(this.heartbeatTimeout)
    }
    this.heartbeatTimeout = null

    this.unbindNetworkListeners()

    this.socket?.removeAllListeners()
    this.socket?.close()
    this.socket = null
    this._isConnected = false
    this.pendingMessages = []
  }

  // 网络断开：主动断开 socket（对齐 android 断网 _innerClose）。
  // 避免依赖 socket.io 退避重连，立即清理连接；onDisconnect 会上报 signal_disconnect。
  private handleNetworkOffline(): void {
    if (this.socket === null) {
      return
    }
    console.log("Signaling network offline, close socket proactively")
    this.socket?.removeAllListeners()
    this.socket?.close()
    this.socket = null
    this._isConnected = false
    if (this.heartbeatTimeout !== null) {
      window.clearTimeout(this.heartbeatTimeout)
    }
    this.heartbeatTimeout = null
  }

  // 网络恢复：主动重建连接（对齐 android 恢复 _innerConnect）。
  private handleNetworkOnline(): void {
    if (this.socket !== null || this.localDevId === null) {
      return
    }
    console.log("Signaling network online, reconnect proactively")
    this.connect(this.localDevId, this.savedToken ?? "", this.remoteDevId ?? "")
  }

  private bindNetworkListeners(): void {
    if (this.networkBound) {
      return
    }
    this.networkBound = true
    window.addEventListener("offline", this.onNetworkOffline)
    window.addEventListener("online", this.onNetworkOnline)
  }

  private unbindNetworkListeners(): void {
    if (!this.networkBound) {
      return
    }
    this.networkBound = false
    window.removeEventListener("offline", this.onNetworkOffline)
    window.removeEventListener("online", this.onNetworkOnline)
  }

  private onDisconnect(reason: string): void {
    console.log("Signaling server disconnected: ", reason)
    this._isConnected = false
    // 上报信令断开，附带断开原因，对齐 android _onSocketDisconnect 的
    // extra: {"msg": event.toString()}
    _reportSDKLog("signal_disconnect", { msg: String(reason) })

    if (reason === "transport close") {
      // 本机设备进入休眠状态
      // this.remotePeerCallback?.onPeerConnect?.(
      //   this.localDevId ?? "",
      //   CONNECT_STATE_CONN_DISCONNECTED
      // )
    }
  }

  private onReconnect(attempt: number): void {
    console.log("Signaling server reconnected, attempt: ", attempt)
    // Socket.IO 的 reconnect 事件发生时底层 socket 已恢复；保持该状态，
    // 使 WebRTC 可以立即恢复 ICE/重新发送 Offer。
    this._isConnected = true
    this.flushPendingMessages()
    this.resolveConnect?.(true)
    this.resolveConnect = null
    // 上报信令重连
    _reportSDKLog("signal_reconnect")
  }

  private onConnectError(error: unknown): void {
    // 对齐 android _onSocketConnectError 的 extra: {"msg": event.toString()}，
    // 统一用对象转字符串，而非仅取 Error.message。
    const msg = String(error)
    console.log("Signaling server socket connection error: ", error)

    // 鉴权失败判定放宽为包含 "unauthorized"，对齐 android message.contains("unauthorized")，
    // 避免服务端文案变动（如 "unauthorized: token error"）导致漏判。
    if (msg.includes("unauthorized")) {
      this.remotePeerCallback?.onSignalingExpired?.()
    }
    this._isConnected = false
    // 上报信令连接失败
    _reportSDKLog("signal_connect_error", { msg })
  }

  setRemotePeerCallback(callback: _OnRemotePeerCallback): void {
    this.remotePeerCallback = callback
  }

  // 监听远程协助消息（独立于 setRemotePeerCallback，生命周期随信令连接常驻）
  // 对齐 android setOnAssistMessage
  setOnAssistMessage(cb: OnAssistMessage): void {
    this.onAssistMessage = cb
  }

  // 发送信令数
  send(event: string, data: any): void {
    if (this.socket === null) {
      // console.warn("Signaling drop data, socket is null", { event })
      return
    }
    if (!this.socket.connected) {
      // console.warn("Signaling queue data until connected", { event })
      this.pendingMessages.push({ event, data })
      return
    }

    this.socket.emit(event, data)
  }

  // 通过信令服务器发送 message 类型的消息给远端（对齐 android sendMessage）
  // [data] 消息业务体
  // [targetDevId] 目标 deviceId，默认发给 remoteDevId（云电脑）
  //
  // 关键：room 始终是云电脑 room（this.remoteDevId，固定），只有 device_id 随
  // targetDevId 变。服务端按 room 路由消息到云电脑 room，再由 room 内成员按
  // device_id 过滤。若 room 跟着 targetDevId 变（如回复协助时 room=管理后台 id），
  // 消息会发到错误的 room，对端收不到。对齐 android：room 固定，device_id 可变。
  sendMessage(data: Record<string, unknown>, targetDevId?: string): void {
    const msg = {
      // room 固定为云电脑 room（对齐 android "room": remoteDevId）
      room: this.remoteDevId,
      // device_id 随目标变化：指定 targetDevId 则发给该设备，否则发云电脑
      device_id: targetDevId ?? this.remoteDevId,
      // 协助方登录模式下 src 带 -assist 后缀
      src: this.assistMode ? `${this.remoteDevId}-assist` : this.localDevId,
      master: true,
      version: 2,
      data: JSON.stringify(data),
    }
    this.send("message", msg)
  }

  // 回复远程协助请求（对齐 android sendAssistReply）
  // [targetDevId] 回复给哪个 deviceId（管理后台）
  // [accepted] 是否同意
  sendAssistReply(targetDevId: string, accepted: boolean): void {
    const status: KCAssistStatus = accepted ? "confirmed" : "denied"
    this.sendMessage({ type: "assist", status }, targetDevId)
  }

  // 成功连接上信令服务器
  private onConnect(): void {
    assert(this.socket !== null, "Signaling onConnect, socket is null")

    this._isConnected = true
    // 发送事件
    this.socket.emit("login", this.localDevId)
    this.socket.emit("heartbeat", this.localDevId)

    this.flushPendingMessages()

    // 上报信令连接成功
    _reportSDKLog("signal_connected")
    this.resolveConnect?.(true)
    this.resolveConnect = null
  }

  private flushPendingMessages(): void {
    if (this.socket === null || !this.socket.connected) return
    const pendingMessages = this.pendingMessages
    this.pendingMessages = []
    pendingMessages.forEach(({ event, data }) => this.socket?.emit(event, data))
  }

  // 发送心跳包
  private onHeartBeat(): void {
    console.log("Signaling receive heartbeat")
    if (this.heartbeatTimeout !== null) {
      window.clearTimeout(this.heartbeatTimeout)
    }
    this.heartbeatTimeout = window.setTimeout(() => {
      // 发送前校验连接状态，断连瞬间不抛异常（对齐 android if (isConnected)）
      if (!this._isConnected || this.socket === null) {
        return
      }
      console.log("Signaling send heartbeat")
      this.socket.emit("heartbeat", this.localDevId)
    }, 30000)
  }

  private onStatus(status: Status): void {
    const { online, room } = status
    console.log(`Signaling receive status, room: ${room}, online: ${online}`)
    // if (!online) {
    // this.remotePeerCallback?.onPeerConnect?.(room, CONNECT_STATE_PEER_NOT_FOUND)
    // // 上报远端设备不在线
    // reportLog("signal_agent_online_err")
    // return
    // }
    // this.remotePeerCallback?.onPeerConnect(room, CONNECT_STATE_REQ_PEER_PWD)
  }

  private onMessage(msg: _SignalingMessage): void {
    // 解析信封：data 可能是 JSON string 或 object（对齐 android envelope["data"]）
    const envelope = msg as unknown as Record<string, unknown>
    const raw = envelope["data"]
    let data: any
    try {
      data =
        typeof raw === "string"
          ? JSON.parse(raw)
          : (raw as Record<string, unknown>)
    } catch (e) {
      console.error("Signaling receive message, parse data error: ", e)
      return
    }

    const type = data?.type
    if (type === undefined) {
      console.error("Signaling receive message, type is undefined")
      return
    }

    const src = (envelope["src"] as string | undefined)?.toString() ?? null
    const dest = (envelope["device_id"] as string | undefined)?.toString()

    console.log(`Signaling receive ${type} message`)

    // assist 消息走独立通道，不检查 dest（对齐 android：任何发送到该 room 的
    // assist 消息都会被处理，通过 src 传递发送方信息）
    if (type === "assist") {
      const status = assistStatusFromValue(data?.status)
      if (status === null) {
        console.warn(`Signaling unknown assist status: ${data?.status}`)
        return
      }
      this.onAssistMessage?.(status, data, src)
      return
    }

    // 非 assist 消息检查 dest 是否是自己，避免多设备同 room 时误处理
    // （对齐 android if (dest != localDevId) return）
    // 协助模式下 agent 回复的 answer/candidate 的 dest 为云电脑 deviceId（即
    // remoteDevId，实测见 sendMessage 的 room/device_id 路由），而非协助方信令
    // 身份 ${remoteDevId}-assist，故协助模式按 remoteDevId 匹配。
    const expectedDest = this.assistMode ? this.remoteDevId : this.localDevId
    if (dest !== expectedDest) {
      return
    }

    if (type === "answer") {
      // 接收到 answer
      const answer = { sdp: data.sdp, type: data.type }
      this.remotePeerCallback?.onAnswer(answer)
      return
    }

    if (type === "candidate") {
      this.remotePeerCallback?.onIceCandidate(data as RTCIceCandidateInit)
      return
    }

    console.warn(`Signaling unknown message: ${JSON.stringify(data)}`)
  }

  // 等待信令在指定时间内连接成功
  async waitUntilConnected(timeoutMs = 60000): Promise<boolean> {
    if (this._isConnected) {
      return true
    }
    if (this.socket === null) {
      // 未调用 connect 无法主动建立连接
      return false
    }

    let timer: number | null = null
    return await new Promise<boolean>((resolve) => {
      this.resolveConnect = resolve

      timer = window.setTimeout(() => {
        resolve(this._isConnected)
      }, timeoutMs)
    }).then((res) => {
      if (timer !== null) {
        window.clearTimeout(timer)
      }
      this.resolveConnect = null
      return res
    })
  }

  // 探测信令连接健康度（对齐 android detectHeartbeat）
  // [count] 探测次数
  // [intervalMs] 间隔
  // 返回：[received, total]
  async detectHeartbeat(
    count = 10,
    intervalMs = 1000
  ): Promise<[number, number]> {
    if (this.socket === null || !this._isConnected) {
      return [0, 0]
    }

    let received = 0
    const handler = (): void => {
      received++
    }

    this.socket.on("heartbeat", handler)
    try {
      for (let i = 0; i < count; i++) {
        this.socket.emit("heartbeat", this.localDevId)
        await new Promise((resolve) => setTimeout(resolve, intervalMs))
      }
      // 等待最后一个包
      await new Promise((resolve) => setTimeout(resolve, 500))
    } finally {
      this.socket.off("heartbeat", handler)
    }

    return [received, count]
  }
}
